import { useGetContext } from '@api/lendingAPI/lapiHooks';
import {
    fetchPostIdCheck,
    fetchPostOnfidoStart,
    GetContextResponse
} from '@iwoca/lapi-client/edge';
import { getQueryParameter, getWindowLocation } from '@iwRouter/LocationHelper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';

import * as IwDataDogLogger from '../../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { TDeserialisedPerson } from '../../../modals/idCheckVerificationModal/IdCheckVerificationModal.types';
import { useGetCreditCardOffer } from '../../hooks/useGetCreditCardOffer';

type TSendVerificationSMSParams = {
    person: TDeserialisedPerson;
    phoneNumber: string;
};

type TSendVerificationSMSResult = {
    sendVerificationSMS: (
        params: TSendVerificationSMSParams
    ) => Promise<Response>;
    isError: boolean;
    error: Error | null;
};

type TSMSVerificationRequestBody = {
    data: {
        person_uid: string;
        phone_uid: string;
        redirect_url?: string;
        offer_id?: string;
    };
};

/**
 * Hook to send verification SMS for ID check
 * Refactored from IdCheckVerificationModal.sagas.js sendSMSWorker
 */
export const useSendVerificationSMS = (): TSendVerificationSMSResult => {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const { creditCardOffer } = useGetCreditCardOffer();
    const { data: site_context } = useGetContext();

    const isIDVerseSplitTest = hasIDVerseSplitTest(site_context);

    const {
        mutateAsync: sendVerificationSMS,
        isError,
        error
    } = useMutation({
        mutationFn: async ({
            person,
            phoneNumber
        }: TSendVerificationSMSParams) => {
            // Find the phone entry matching the selected phone number
            const phoneEntry = _.find(person.phones, {
                value: phoneNumber
            });

            if (!phoneEntry?.uid) {
                throw new Error('Phone entry not found');
            }
            const offerId = creditCardOffer?.id;

            if (!offerId) {
                IwDataDogLogger.logError(
                    'Credit card offer ID not found for ID check SMS',
                    {
                        tags: {
                            reporter: 'useSendVerificationSMS'
                        }
                    }
                );
                throw new Error('Credit card offer ID is required');
            }
            const requestBody: TSMSVerificationRequestBody = {
                data: {
                    person_uid: person.uid,
                    phone_uid: phoneEntry.uid
                }
            };
            let response = null;
            if (isIDVerseSplitTest) {
                const location = getWindowLocation();
                const offerId = getQueryParameter(location, 'offerId');
                requestBody.data.redirect_url = location.href;
                requestBody.data.offer_id = offerId;

                // Call the API
                response = await fetchPostIdCheck({
                    stateKey,
                    //@ts-expect-error:ignore
                    body: requestBody
                });
            } else {
                // Call the API
                response = await fetchPostOnfidoStart({
                    stateKey,
                    body: requestBody
                });
            }

            // Check for maximum attempts error (400 status)
            if (response.status === 400) {
                throw new Error('MAXIMUM_ATTEMPTS_REACHED');
            }

            if (!response.ok) {
                throw new Error('Failed to send verification SMS');
            }

            return response;
        },
        onSuccess: async () => {
            // Invalidate onboarding requirements to refresh the ID check status
            await queryClient.invalidateQueries({
                queryKey: ['credit-card-onboarding-requirements', stateKey]
            });

            // Also invalidate customer state to get updated data
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        },
        onError: (error: Error) => {
            IwDataDogLogger.logError('Failed to send verification SMS', {
                tags: {
                    reporter: 'useSendVerificationSMS',
                    error_message: error.message
                }
            });
        }
    });

    return {
        sendVerificationSMS,
        isError,
        error: error as Error | null
    };
};

const hasIDVerseSplitTest = (
    site_context: GetContextResponse['data']
): boolean => {
    return Boolean(
        site_context?.user_split_tests.some((key) =>
            Boolean(
                'id_verification_provider_idverse' in key &&
                    key.id_verification_provider_idverse
            )
        )
    );
};
